<template>
  <div class="theme-notice-layer" aria-live="polite">
    <transition-group name="notice-toast" tag="div" class="theme-toast-stack">
      <article
        v-for="toast in toasts"
        :key="toast.id"
        :class="['theme-toast', `is-${toast.type}`]"
        role="status"
      >
        <span class="theme-notice-icon" aria-hidden="true">
          <i :class="iconClass(toast.type)" />
        </span>
        <p>{{ toast.message }}</p>
        <button
          v-if="toast.closable"
          type="button"
          :aria-label="$t('notice.close')"
          @click="removeToast(toast.id)"
        >
          <i class="el-icon-close" />
        </button>
        <span class="theme-toast-life" :style="{ animationDuration: `${toast.duration}ms` }" />
      </article>
    </transition-group>

    <transition name="notice-dialog">
      <div
        v-if="activeDialog"
        class="theme-dialog-mask"
        role="presentation"
        @click.self="handleMaskClick"
      >
        <section
          ref="dialog"
          :class="['theme-notice-dialog', `is-${activeDialog.type}`]"
          role="alertdialog"
          aria-modal="true"
          :aria-labelledby="`theme-dialog-title-${activeDialog.id}`"
          :aria-describedby="`theme-dialog-message-${activeDialog.id}`"
          tabindex="-1"
        >
          <div class="theme-dialog-orbit" aria-hidden="true" />
          <span class="theme-dialog-icon" aria-hidden="true">
            <i :class="iconClass(activeDialog.type)" />
          </span>
          <small>{{ dialogEyebrow }}</small>
          <h2 :id="`theme-dialog-title-${activeDialog.id}`">{{ dialogTitle }}</h2>
          <p :id="`theme-dialog-message-${activeDialog.id}`">{{ activeDialog.message }}</p>
          <footer>
            <button
              v-if="activeDialog.showCancelButton"
              type="button"
              class="theme-dialog-cancel"
              @click="settleDialog(false)"
            >
              {{ activeDialog.cancelButtonText || $t("common.cancel") }}
            </button>
            <button
              ref="confirmButton"
              type="button"
              class="theme-dialog-confirm"
              @click="settleDialog(true)"
            >
              {{ activeDialog.confirmButtonText || $t("common.confirm") }}
            </button>
          </footer>
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
import notice from "../utils/notice"

export default {
  name: "ThemeNotice",
  data() {
    return {
      toasts: [],
      dialogQueue: [],
      activeDialog: null,
      unsubscribe: null,
      nextId: 1
    }
  },
  computed: {
    dialogTitle() {
      if (this.activeDialog.title) return this.activeDialog.title
      return this.$t(`notice.${this.activeDialog.type}Title`)
    },
    dialogEyebrow() {
      return this.$t(this.activeDialog.mode === "confirm" ? "notice.confirmEyebrow" : "notice.alertEyebrow")
    }
  },
  created() {
    this.unsubscribe = notice.subscribe(this.handleNotice)
    window.addEventListener("keydown", this.handleKeydown)
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe()
    window.removeEventListener("keydown", this.handleKeydown)
    this.toasts.forEach(toast => window.clearTimeout(toast.timer))
  },
  methods: {
    iconClass(type) {
      return {
        success: "el-icon-check",
        error: "el-icon-close",
        warning: "el-icon-warning-outline",
        info: "el-icon-bell"
      }[type] || "el-icon-bell"
    },
    handleNotice(event) {
      if (event.kind === "toast") {
        this.pushToast(event)
        return
      }
      this.dialogQueue.push({ ...event, id: this.nextId++ })
      this.showNextDialog()
    },
    pushToast(event) {
      const duplicate = this.toasts.find(toast => toast.type === event.type && toast.message === event.message)
      if (duplicate) {
        window.clearTimeout(duplicate.timer)
        duplicate.duration = this.toastDuration(event)
        duplicate.timer = window.setTimeout(() => this.removeToast(duplicate.id), duplicate.duration)
        return
      }

      const toast = {
        ...event,
        id: this.nextId++,
        duration: this.toastDuration(event),
        timer: null
      }
      toast.timer = window.setTimeout(() => this.removeToast(toast.id), toast.duration)
      this.toasts.push(toast)
      if (this.toasts.length > 3) this.removeToast(this.toasts[0].id)
    },
    toastDuration(event) {
      if (Number(event.duration) > 0) return Number(event.duration)
      return event.type === "error" ? 4200 : 2800
    },
    removeToast(id) {
      const index = this.toasts.findIndex(toast => toast.id === id)
      if (index < 0) return
      window.clearTimeout(this.toasts[index].timer)
      this.toasts.splice(index, 1)
    },
    showNextDialog() {
      if (this.activeDialog || !this.dialogQueue.length) return
      this.activeDialog = this.dialogQueue.shift()
      this.$nextTick(() => {
        const target = this.$refs.confirmButton || this.$refs.dialog
        if (target) target.focus()
      })
    },
    settleDialog(confirmed) {
      if (!this.activeDialog) return
      const dialog = this.activeDialog
      this.activeDialog = null
      if (confirmed) dialog.resolve("confirm")
      else dialog.reject("cancel")
      this.$nextTick(this.showNextDialog)
    },
    handleMaskClick() {
      if (this.activeDialog && this.activeDialog.closeOnClickModal) this.settleDialog(false)
    },
    handleKeydown(event) {
      if (!this.activeDialog) return
      if (event.key === "Escape") this.settleDialog(false)
      if (event.key === "Enter") this.settleDialog(true)
    }
  }
}
</script>

<style scoped>
.theme-notice-layer {
  position: fixed;
  z-index: 12000;
  inset: 0;
  pointer-events: none;
}

.theme-toast-stack {
  position: absolute;
  top: max(18px, env(safe-area-inset-top));
  left: 50%;
  display: grid;
  gap: 10px;
  width: min(calc(100vw - 28px), 390px);
  transform: translateX(-50%);
}

.theme-toast {
  --notice-color: #59e6c2;
  position: relative;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 26px;
  gap: 10px;
  align-items: center;
  min-height: 58px;
  overflow: hidden;
  padding: 10px 10px 10px 12px;
  border: 1px solid rgba(255,255,255,.11);
  border-radius: 18px;
  color: #f8f8fa;
  background: linear-gradient(120deg, rgba(29,31,39,.97), rgba(12,13,18,.96));
  box-shadow: 0 18px 55px rgba(0,0,0,.42), inset 0 1px rgba(255,255,255,.06);
  backdrop-filter: blur(20px);
  pointer-events: auto;
}

.theme-toast.is-error, .theme-notice-dialog.is-error { --notice-color: #ff4963; }
.theme-toast.is-warning, .theme-notice-dialog.is-warning { --notice-color: #ffbf5a; }
.theme-toast.is-info, .theme-notice-dialog.is-info { --notice-color: #67dbea; }

.theme-notice-icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(255,255,255,.18);
  border-radius: 50%;
  color: var(--notice-color);
  background: rgba(255,255,255,.055);
  box-shadow: 0 0 22px rgba(255,255,255,.05);
}

.theme-toast p {
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.55;
}

.theme-toast button {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  color: #7e818b;
  background: rgba(255,255,255,.05);
}

.theme-toast-life {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--notice-color));
  transform-origin: left;
  animation: notice-life linear forwards;
}

.theme-dialog-mask {
  position: absolute;
  display: grid;
  place-items: center;
  padding: 20px;
  inset: 0;
  background: rgba(4,5,8,.72);
  backdrop-filter: blur(9px);
  pointer-events: auto;
}

.theme-notice-dialog {
  --notice-color: #ff5368;
  position: relative;
  width: min(100%, 356px);
  overflow: hidden;
  padding: 26px 24px 22px;
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 26px;
  color: #fff;
  background: radial-gradient(circle at 88% 0, rgba(255,83,104,.2), transparent 34%), linear-gradient(145deg, #1b1d25, #0e0f14 72%);
  box-shadow: 0 35px 100px rgba(0,0,0,.68), inset 0 1px rgba(255,255,255,.07);
  outline: 0;
}

.theme-dialog-orbit {
  position: absolute;
  top: -75px;
  right: -68px;
  width: 170px;
  height: 170px;
  border: 1px solid rgba(255,255,255,.09);
  border-radius: 50%;
  box-shadow: inset 0 0 45px rgba(255,83,104,.08);
}

.theme-dialog-icon {
  display: grid;
  place-items: center;
  width: 47px;
  height: 47px;
  border: 1px solid rgba(255,255,255,.16);
  border-radius: 16px;
  color: var(--notice-color);
  background: rgba(255,255,255,.055);
  font-size: 21px;
  transform: rotate(-4deg);
}

.theme-notice-dialog > small {
  display: block;
  margin-top: 20px;
  color: var(--notice-color);
  font-size: 8px;
  font-weight: 900;
  letter-spacing: 2.2px;
  text-transform: uppercase;
}

.theme-notice-dialog h2 {
  margin: 7px 0 0;
  overflow-wrap: anywhere;
  font-size: 21px;
  line-height: 1.3;
}

.theme-notice-dialog > p {
  margin: 10px 0 0;
  color: #a9acb5;
  overflow-wrap: anywhere;
  font-size: 12px;
  line-height: 1.7;
  white-space: pre-line;
}

.theme-notice-dialog footer {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
  margin-top: 24px;
}

.theme-notice-dialog footer button {
  min-width: 0;
  min-height: 44px;
  padding: 10px 12px;
  border-radius: 13px;
  overflow-wrap: anywhere;
  font-size: 11px;
  font-weight: 900;
}

.theme-dialog-cancel {
  color: #c2c4ca;
  border: 1px solid rgba(255,255,255,.1);
  background: rgba(255,255,255,.055);
}

.theme-dialog-confirm {
  color: #fff;
  background: linear-gradient(110deg, var(--notice-color), #ff7658);
  box-shadow: 0 10px 26px rgba(255,83,104,.24);
}

.theme-notice-dialog footer .theme-dialog-confirm:only-child { grid-column: 1 / -1; }
.notice-toast-enter-active, .notice-toast-leave-active { transition: opacity .24s ease, transform .28s cubic-bezier(.2,.8,.2,1); }
.notice-toast-enter, .notice-toast-leave-to { opacity: 0; transform: translateY(-18px) scale(.96); }
.notice-dialog-enter-active, .notice-dialog-leave-active { transition: opacity .22s ease; }
.notice-dialog-enter-active .theme-notice-dialog, .notice-dialog-leave-active .theme-notice-dialog { transition: transform .28s cubic-bezier(.2,.8,.2,1), opacity .22s ease; }
.notice-dialog-enter .theme-notice-dialog, .notice-dialog-leave-to .theme-notice-dialog { opacity: 0; transform: translateY(24px) scale(.94); }

@keyframes notice-life { to { transform: scaleX(0); } }

[dir="rtl"] .theme-toast { grid-template-columns: 26px minmax(0, 1fr) 34px; padding: 10px 12px 10px 10px; }
[dir="rtl"] .theme-toast .theme-notice-icon { grid-column: 3; }
[dir="rtl"] .theme-toast p { grid-column: 2; grid-row: 1; text-align: right; }
[dir="rtl"] .theme-toast button { grid-column: 1; grid-row: 1; }
[dir="rtl"] .theme-notice-dialog { text-align: right; }
[dir="rtl"] .theme-dialog-orbit { right: auto; left: -68px; }

@media (max-width: 520px) {
  .theme-dialog-mask { align-items: end; padding: 12px 12px max(12px, env(safe-area-inset-bottom)); }
  .theme-notice-dialog { width: 100%; border-radius: 24px; }
}

@media (prefers-reduced-motion: reduce) {
  .theme-toast-life { animation: none; }
}
</style>
